/**
 * PRD §7-2 활성/비활성 매트릭스 — 서버 규칙과 같은 정의. 비활성은 사유 툴팁으로 보여 준다.
 * 단 `unsupportedType` 은 메뉴가 항목 자체를 뺀다.
 *
 * 액션별 자격 = 지원 유형 + 막는 규칙 목록. 규칙은 BLOCKING_RULES 에 한 번만 정의하고,
 * 목록 순서가 곧 사유 우선순위다(먼저 걸린 사유 하나만 보인다).
 */
import { MAX_QUIZ_COUNT } from '../../../constant';
import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { QUESTION_CARD_AI_QUIZ_TYPES } from '../consts/questionCardAi.const';
import {
  type Eligibility,
  type EligibilityReason,
} from '../types/questionCardAi.types';

import {
  isSlotFilled,
  SLOT_QUIZ_TYPES,
  slotsOf,
} from './questionCardAiSlotSchema';

export type EligibilityTarget = {
  quiz: BuildQuizListItem;
  count: number;
  maxQuizCount?: number;
};

/**
 * 이미지가 의미를 지는 입력은 서버가 이미지를 받을 때까지 막는다.
 * 스냅샷 직렬화는 문제 이미지를 보내지 않고, 이미지 전용 정답은 `(image option)` 자리표시로만
 * 나가므로 "그림 속 도형의 각도는?" 류는 정답 의미가 빠진 채 생성된다.
 */
const dependsOnImage = (quiz: BuildQuizListItem): boolean =>
  !!quiz.questionImageUrl ||
  slotsOf(quiz).some(
    slot => slot.isAnswer && !slot.text.trim() && !!slot.imageUrl,
  );

/** 사유 → "이 조건이면 막는다". */
const BLOCKING_RULES = {
  emptyQuestion: ({ quiz }) => !quiz.question?.trim(),
  imageNotSupported: ({ quiz }) => dependsOnImage(quiz),
  quizLimit: ({ count, maxQuizCount = MAX_QUIZ_COUNT }) =>
    count >= maxQuizCount,
  // 채움 대상은 "이미 비어 있는 슬롯" 뿐이다 — 새로 만들지 않는다.
  // 정답 지정 여부·정답으로 찍힌 빈 칸도 막지 않는다. 정답이 없으면 서버가 후보 모드로,
  // 정답으로 찍힌 빈 칸이 있으면 그 자리를 정답으로 만들고 나머지 빈 칸을 오답으로 채운다.
  noEmptySlots: ({ quiz }) => !slotsOf(quiz).some(slot => !isSlotFilled(slot)),
} satisfies Partial<
  Record<EligibilityReason, (target: EligibilityTarget) => boolean>
>;

type BlockingReason = keyof typeof BLOCKING_RULES;

const defineEligibility =
  (
    supportedTypes: readonly BuildQuizListItem['type'][],
    blockedWhen: readonly BlockingReason[],
  ) =>
  (target: EligibilityTarget): Eligibility => {
    if (!supportedTypes.includes(target.quiz.type))
      return { enabled: false, reason: 'unsupportedType' };
    const reason = blockedWhen.find(rule => BLOCKING_RULES[rule](target));
    return reason ? { enabled: false, reason } : { enabled: true };
  };

/** 어떤 AI 액션도 다루지 않는 유형(짝맞추기·순서맞추기)이면 메뉴 항목이 통째로 빈다. */
export const isQuestionCardAiQuizType = (type: BuildQuizListItem['type']) =>
  (QUESTION_CARD_AI_QUIZ_TYPES as readonly string[]).includes(type);

export const resolveSimilarEligibility = defineEligibility(
  QUESTION_CARD_AI_QUIZ_TYPES,
  ['emptyQuestion', 'imageNotSupported', 'quizLimit'],
);

export const resolveFillOptionsEligibility = defineEligibility(
  SLOT_QUIZ_TYPES,
  ['emptyQuestion', 'imageNotSupported', 'noEmptySlots'],
);
